import _ from 'lodash';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import { getUser } from './user';
import { subscriptions } from './subscriptions';

export const getMachinesForUser = store => _.get(store, 'common.machines');
const getInvites = store => _.get(store, 'accountSettings.pendingInvites');

// Guests get machine blobs that put owner in an 'owner' field. Managers may see
// a list of user blobs that include users with role owner, so look in both
// places for owners.
const getMachineOwners = (machine) => {
  const owners = _.filter(machine.users, u => _.includes(u.roles, 'owner'));
  const machineOwner = _.get(machine, 'owner');

  if (machineOwner && !_.find(owners, u => u.email === machineOwner.email)) {
    owners.push(machineOwner);
  }

  return owners;
};

function groomMachine(user, machine) {
  const machineUser = _.find(machine.users, u => u.email === user.email) || {};
  const machineOwners = getMachineOwners(machine);

  return {
    machineName: machine.display_name,
    serial: machine.serial,
    model: machine.model,
    ownerName: _.join(_.map(machineOwners, _.property('name')), ', '),
    ownerEmails: _.map(machineOwners, _.property('email')),
    isOwner: _.includes(machineUser.roles, 'owner'),
    isAdmin: _.includes(machineUser.roles, 'manager'),
    abilities: machine.abilities || null,
    lastPrintAt: machine.last_print_at || null,
    userCount: _.size(machine.users),
  };
}

export const getManagedMachinesForUser = createSelector(
  getMachinesForUser,
  machines => _.filter(machines, machine =>
    _.includes(
      _.get(machine, 'abilities'),
      'manage_access_to')),
);

export const getMachineList = createSelector(
  getUser,
  getMachinesForUser,
  (user, machines) => _.map(machines, machine => groomMachine(user, machine)),
);

export const getManagedMachineList = createSelector(
  getUser,
  getManagedMachinesForUser,
  (user, machines) => _.map(machines, machine => groomMachine(user, machine)),
);

// If we only have one machines we set it as the initial form value
export const getInitialMachineForInvite = createSelector(
  getMachineList,
  (machines) => {
    if (machines.length === 1) {
      return {
        // This needs to be manager for the legacy UX, but should be empty when
        // cleaning up USER_ROLE_MANAGEMENT flag.
        machines: [{ serial: machines[0].serial, roles: ['manager'] }],
      };
    }
    return null;
  },
);

export const getAllUsersForCurrentUsersMachines = createSelector(
  getUser,
  getManagedMachinesForUser,
  subscriptions.getAddedUsers,
  (currentUser, machines, addedUsers) => {
    const userMap = {};
    _.forEach(machines, (machine) => {
      _.forEach(machine.users, (user) => {
        if (user.email === currentUser.email) {
          return;
        }
        if (!userMap[user.email]) {
          userMap[user.email] = { ...user, machines: [] };
        }
        userMap[user.email].machines.push({
          machineName: machine.display_name,
          serial: machine.serial,
          roles: user.roles });
      });
    });

    // merge with addedUsers who are not also machine users
    _.forEach(addedUsers, (user) => {
      const email = _.get(user, 'attributes.secondary_user_email', '');
      if (email && !userMap[email]) {
        userMap[email] = {
          email,
          machines: [],
          name: email, // addedUser data only has email
          lastPrintAt: '',
          addedAt: '',
        };
      }
    });

    const userList = [];
    _.reduce(userMap, (results, user) => {
      results.push({
        name: user.name,
        addedAt: user.added_at,
        lastPrintAt: user.last_print_at,
        email: user.email,
        machines: user.machines,
      });
      return results;
    }, userList);

    return userList;
  },
);

export const getSortedAllUsersForCurrentUsersMachines = createSelector(
  getAllUsersForCurrentUsersMachines,
  users => (_.sortBy(users, u => u.name)),
);

export const getPendingInvites = createSelector(
  getInvites,
  getMachinesForUser,
  (pending, machines) => {
    const userMap = {};
    _.forEach(pending, (invite) => {
      const machine = _.find(machines, { serial: invite.machine_serial });

      // Note: if invited someone but then lost access to the machine you invited them too
      // we don't show the pending invitation any more.
      if (!userMap[invite.invited_email] && machine) {
        userMap[invite.invited_email] = { ...invite, machines: [] };
      }

      if (machine) {
        userMap[invite.invited_email].machines.push({
          machineName: machine.display_name,
          serial: machine.serial,
        });
      }
    });

    const results = [];
    _.reduce(userMap, (result, invite) => {
      result.push({
        createdAt: invite.created_at,
        email: invite.invited_email,
        id: invite.id,
        machines: invite.machines,
      });
      return result;
    }, results);

    return results;
  },
);

export const machinePropType = {
  machineName: PropTypes.string,
  serial: PropTypes.string,
  isOwner: PropTypes.bool,
  ownerName: PropTypes.string,
  ownerEmails: PropTypes.arrayOf(PropTypes.string),
  isAdmin: PropTypes.bool,
  roles: PropTypes.arrayOf(PropTypes.string),
  abilities: PropTypes.arrayOf(PropTypes.string),
};

export const machineUsersPropType = {
  displayName: PropTypes.string,
  name: PropTypes.string,
  email: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.string),
  machines: PropTypes.arrayOf(PropTypes.shape(machinePropType)),
};

export const pendingInvitesPropTypes = {
  created_at: PropTypes.string,
  email: PropTypes.string,
  machins: PropTypes.arrayOf(PropTypes.string),
};



// WEBPACK FOOTER //
// ./app/selectors/machines.js